import React from 'react';
import type { ViewProps } from 'react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Aside } from '@expo/html-elements';
import {
  Container,
  theme,
  useResponsive,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';

import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';
import { PageWrapper } from '../../PageWrapper';
import { AccountMenu } from '../AccountMenu';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountScreenContainer = (props: AccountScreenContainerProps) => {
  const { screen: screenId, testID, children } = props;

  const { currentBreakpoint, minWidth, match } = useResponsive();
  const isLoggedIn = useIsLoggedIn();

  // ─── STYLES ──────────────────────────────────────────────────────

  const menuWrapperStyles = [
    styles.menuWrapper,
    match([{}, styles.menuWrapperSM, styles.menuWrapperMD]),
  ];
  const tabContentStyles = [
    styles.tabContent,
    minWidth.largeDisplay ? styles.tabContentLG : {},
  ];

  // ─────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return <AccountSignedOutMessage redirectTo={screenId} />;
  }

  if (currentBreakpoint.isMobile) {
    return (
      <AccountScreenContainerPageWrapper testID={testID}>
        <Container wrapperStyle={styles.containerXS} style={styles.containerXS}>
          {children}
        </Container>
      </AccountScreenContainerPageWrapper>
    );
  }

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <Container
        customSpacing={0}
        wrapperStyle={styles.containerSM}
        style={styles.containerSM}
      >
        <View style={styles.contentWrapper}>
          <Aside style={menuWrapperStyles}>
            <AccountMenu screen={screenId} variant="SIDEBAR" />
          </Aside>

          <ScrollView
            contentContainerStyle={styles.tabContentContainer}
            style={tabContentStyles}
          >
            {children}

            {/* Because browsers ignore padding in scroll containers,
                bottom spacing is added using an additional component. */}
            <View style={styles.tabContentBottomSpacer} />
          </ScrollView>
        </View>
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const AccountScreenContainerPageWrapper = (
  props: Pick<ViewProps, 'children' | 'testID'>,
) => {
  const contentContainerStyle = webOnlyStyles({ height: '100%' });

  return (
    <PageWrapper
      testID={props.testID}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={contentContainerStyle}
      style={styles.pageWrapper}
      withHeaderTitle
    >
      {props.children}
    </PageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    height: '100%',
    backgroundColor: theme.colors.OATMEAL,
  },
  contentWrapper: {
    flexDirection: 'row',
    height: '100%',
  },
  containerXS: {
    minHeight: '100%',
    paddingBottom: theme.spacing.large,
  },
  containerSM: {
    minHeight: 700,
    height: '100%',
  },
  tabContentContainer: {
    maxHeight: '100%',
    paddingTop: 48,
    paddingHorizontal: theme.spacing.xlarge,
  },
  tabContentBottomSpacer: {
    height: theme.spacing.xlarge,
  },
  tabContent: {
    flex: 1,
    minHeight: '100%',
  },
  tabContentLG: {
    paddingHorizontal: 100,
  },
  menuWrapper: {
    paddingVertical: 32,
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
  },
  menuWrapperSM: {
    width: 280,
    paddingHorizontal: theme.spacing.xlarge,
  },
  menuWrapperMD: {
    width: 320,
    paddingHorizontal: theme.spacing.xlarge,
  },
});

// ────────────────────────────────────────────────────────────────────────────────

export type AccountScreenContainerProps = {
  readonly screen: AccountTabScreenId;
} & ViewProps;
